import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import GallerySection from "../components/GallerySection";
import { fetchCollection, getCompanyBadge } from "../lib/cms";

const CLIENT_PROJECTS = [
  {
    title: "Ongoing work",
    case_key: "ongoing-work",
    category: "Current",
    text: "<p>At the moment, I split my time between my own AI projects and helping a few customers with their projects. Details on request.</p>"
  },
  {
    title: "Prajaya",
    case_key: "prajaya",
    category: "E-commerce",
    text: "<p>Prajaya is a Danish brand selling handcrafted singing bowls, statues and other crafts from artisans in Nepal and beyond. I built and maintain their online store, prajaya.dk, on the Shopify platform. Alongside the build, I run their SEO, Google and Meta ads, and social media marketing, learning the growth side of e-commerce hands-on. The store is bilingual, ships to several EU markets, and serves both B2C and B2B customers.</p>"
  },
  {
    title: "Nordic Fintech Week",
    case_key: "nordic-fintech-week",
    category: "Fintech",
    text: "<p>I built custom WordPress plugins for Copenhagen Fintech's Nordic Fintech Week 2026 website that show the event programme and speakers.</p>"
  },
  {
    title: "Tokerød Plus",
    case_key: "tokeroed-plus",
    category: "Software development agency",
    text: "<p>I was part of a small team taking customer projects from idea and development through to deployment, including CarMatch and SmartBeboer. I also did technical architecture advisory work for customers such as Energi Fyn.</p><p class=\"mt-4\">The piece I'm proudest of is a custom-built plugin for SmartBeboer that paid out money to users automatically through Danske Bank's secure business-banking web services.</p>"
  },
  {
    title: "Dansk Motor Finans",
    case_key: "dansk-motor-finans",
    category: "Autotech",
    text: "<p>I extended DMF's online payments to cover more of the ways Danish car dealerships actually pay through the company portal. I also built an internal tool that took over complex manual work for a business-critical team.</p>"
  },
  {
    title: "QuadSAT Cloud",
    case_key: "quadsat-cloud",
    category: "Satellite Communications",
    text: "<p>QuadSAT's antenna measurement for ground stations ran only as desktop software. I designed and built the first version of its cloud platform: the APIs, user management and data pipeline that connect to the desktop tool and store measurement data centrally. I defined what the platform would be before any of it existed.</p>"
  },
  {
    title: "Grundfos iSOLUTIONS Platform",
    case_key: "grundfos-isolutions-platform",
    category: "Water Technology",
    text: "<p>I was on the team that took Grundfos's iSOLUTIONS platform live for the first time. I built the core UI components and the data visualisation layer. I also proposed a modular frontend architecture so several teams could ship on the same platform without blocking each other.</p>"
  }
];

export default function Work() {
  const [projects, setProjects] = useState(CLIENT_PROJECTS);

  useEffect(() => {
    async function loadCases() {
      const data = await fetchCollection("case_studies");
      if (Array.isArray(data) && data.length > 0) {
        // Map CMS entries to the 6 client projects by key/title, keeping exact client order
        const cmsMap = new Map();
        data.forEach((item) => {
          if (item.title) cmsMap.set(item.title.toLowerCase().trim(), item);
          if (item.case_key) cmsMap.set(item.case_key.toLowerCase().trim(), item);
        });

        const resolved = CLIENT_PROJECTS.map((cp) => {
          const fromCms = cmsMap.get(cp.title.toLowerCase().trim()) || cmsMap.get(cp.case_key.toLowerCase().trim());
          if (fromCms && fromCms.solution) {
            return {
              ...cp,
              id: fromCms.id,
              category: fromCms.category || cp.category,
              text: fromCms.solution
            };
          }
          return cp;
        });
        setProjects(resolved);
      }
    }
    loadCases();
  }, []);

  return (
    <>
      <SEOHead
        title="Featured Work - Prajwal Halwai"
        description="Real engineering for real business problems, from architecture and cloud platforms to web applications, payment integrations and e-commerce."
      />

      {/* Hero Header */}
      <section className="py-20 md:py-24 border-b border-[#181B24] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
              SELECTED PROJECTS
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
              Featured Work
            </h1>
            <p className="text-base sm:text-lg text-[#94A3B8] leading-relaxed">
              Real engineering for real business problems, from architecture and cloud platforms to web applications, payment integrations and e-commerce.
            </p>
          </div>
        </div>
      </section>

      {/* Selected Projects List */}
      <section className="py-20 border-b border-[#181B24]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {projects.map((p) => {
            const badge = getCompanyBadge(p);
            return (
              <div
                key={p.case_key || p.id || p.title}
                className="rounded-2xl bg-[#11141C] border border-[#202533] p-8 sm:p-12 space-y-6"
              >
                <div className="space-y-6">
                  <div>
                    {badge && (
                      <div className="mb-3">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide border ${badge.className}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${badge.dotClassName}`} />
                          {badge.label}
                        </span>
                      </div>
                    )}
                    <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                      {p.title}
                    </h2>
                  </div>

                  <div className="space-y-4 text-sm sm:text-base text-[#CBD5E1] leading-relaxed">
                    <div 
                      dangerouslySetInnerHTML={{ __html: p.text || p.solution }}
                      className="[&>p]:leading-relaxed"
                    />
                  </div>

                  <div className="pt-2">
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-[#CC3737] hover:bg-[#b52f2f] transition-all"
                    >
                      <span>Discuss similar project</span>
                      <i className="fa-solid fa-arrow-right text-xs" />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Connected Project Artifacts Gallery */}
      <GallerySection
        gallerySlug="home-gallery"
        title="Project & Architecture Artifacts"
        subtitle="Visual records of development environments, code reviews, and deployments."
      />

      {/* Authentic Call to Action Banner */}
      <section className="py-20 text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#CC3737]">
            Work with Prajwal
          </span>
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
            Ready to start a project?
          </h2>
          <p className="text-base text-[#94A3B8]">
            I'm available for hire or freelance worldwide. Let's discuss your timeline and roadmap.
          </p>
          <div className="pt-2">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-xl text-sm font-semibold text-white bg-[#CC3737] hover:bg-[#b52f2f] transition-all shadow-lg shadow-[#CC3737]/25"
            >
              <span>Get in touch</span>
              <i className="fa-solid fa-arrow-right text-xs" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
